import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Field, FormConsumer, FormProvider, ObjectField, observer } from "@formily/react";
import { Button } from "components/core/basic/Button";
import Loading from "components/core/blocks/Loading";
import { useFormily } from "hooks/useFormily";

import { onFieldInputValueChange } from "@formily/core";
import { useQuery } from "@tanstack/react-query";
import { DevErrorDisplay } from "components/core/blocks/DevErrorDisplay";
import { endOfMonth, formatISO, startOfMonth, subMonths, subYears } from "date-fns";
import { useRef, useState } from "react";
import { Dialog, Popover, TooltipTrigger } from "react-aria-components";
import { MdOutlineIosShare } from "react-icons/md";
import { css, cx } from "../../../styled-system/css";
import { Center, Grid, HStack, VStack } from "../../../styled-system/jsx";
import { Modal } from "../../components/core/basic/RACModal";
import { OverlayArrow, Tooltip } from "../../components/core/basic/Tooltip";
import { Datepicker } from "../../components/fields/base/Datepicker";
import { CurrencyInput, NumberInput, OffsetInput } from "../../components/fields/base/NumberInput";
import { Select } from "../../components/fields/base/Select";
import {
  CurrencyInput as CurrencyInputF,
  NumberInput as NumberInputF,
} from "../../components/fields/formily/NumberInput";
import { inputClassNames, popoverStyles } from "../../components/fields/styles/styles";
import { useAppSelector } from "../../redux/hooks";
import type { Offset } from "../../zodios/apiDefinitions/pricing";
import { useGetV4ProductReportTotalsOpen } from "../perf_reports/api/product/v4ProductReportTotals";
import { applyOffset } from "../pricingv2/utils";
import { useCategoryGoalsQueryOptions } from "./api/useCategoryGoalsQueryOptions";
import { useOrgGoalsQueryOptions } from "./api/useOrgGoalsQueryOptions";
import { useSetCategoryGoals } from "./api/useSetCategoryGoals";
import { useSetOrgGoals } from "./api/useSetOrgGoals";
import { vstack } from "../../../styled-system/patterns";

interface Goal {
  total_sales: number;
  tacos: number;
  ad_spend: number;
}

export const OrgGoalsConfigModal = NiceModal.create(() => {
  const modal = useModal();
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());

  const orgGoalQuery = useQuery({
    ...useOrgGoalsQueryOptions(
      formatISO(new Date(displayYear, 0, 1), {
        representation: "date",
      }),
      formatISO(new Date(displayYear, 11, 31), {
        representation: "date",
      }),
    ),
    select(data) {
      // Transform the data into a record of goals per period
      return data.reduce(
        (acc, goal) => {
          acc[goal.start_date] = {
            total_sales: goal.total_sales_goal,
            tacos: (goal.ad_spend_goal / goal.total_sales_goal) * 100,
            ad_spend: goal.ad_spend_goal,
          };
          return acc;
        },
        {} as Record<string, Goal>,
      );
    },
    refetchOnMount: true,
  });

  const setGoalsMutation = useSetOrgGoals();

  return (
    <Modal isOpen={modal.visible} title={"Configure Goals"} onOpenChange={(isOpen) => !isOpen && modal.remove()}>
      <YearControl setDisplayYear={setDisplayYear} displayYear={displayYear} />
      {orgGoalQuery.isLoading || !orgGoalQuery.isFetchedAfterMount ?
        <Loading />
      : orgGoalQuery.isSuccess ?
        <GoalsConfigForm
          initialValues={orgGoalQuery.data}
          onSubmit={async (values) => {
            const payload = Object.entries(values)
              .filter(([, goal]) => !Number.isNaN(goal.total_sales) && !Number.isNaN(goal.ad_spend))
              .map(([start_date, goal]) => ({
                start_date,
                total_sales_goal: goal.total_sales,
                ad_spend_goal: goal.ad_spend,
                market_place: marketPlace,
              }));
            await setGoalsMutation.mutateAsync(payload);
          }}
          displayYear={displayYear}
        />
      : <DevErrorDisplay userMessage="There was an error loading the goal configuration" error={orgGoalQuery.error} />}
    </Modal>
  );
});

export const CategoryGoalsConfigModal = NiceModal.create(({ categoryId }: { categoryId: number }) => {
  const modal = useModal();
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());

  const categoryGoalQuery = useQuery({
    ...useCategoryGoalsQueryOptions(
      formatISO(new Date(displayYear, 0, 1), {
        representation: "date",
      }),
      formatISO(new Date(displayYear, 11, 31), {
        representation: "date",
      }),
    ),
    select(data) {
      // Transform the data into a record of goals per period
      return data.reduce(
        (acc, goal) => {
          if (goal.category_id !== categoryId) return acc;

          acc[goal.start_date] = {
            total_sales: goal.total_sales_goal,
            tacos: (goal.ad_spend_goal / goal.total_sales_goal) * 100,
            ad_spend: goal.ad_spend_goal,
          };
          return acc;
        },
        {} as Record<string, Goal>,
      );
    },
    refetchOnMount: true,
  });

  const setGoalsMutation = useSetCategoryGoals();

  return (
    <Modal isOpen={modal.visible} title={"Configure Goals"} onOpenChange={(isOpen) => !isOpen && modal.remove()}>
      <YearControl setDisplayYear={setDisplayYear} displayYear={displayYear} />
      {categoryGoalQuery.isLoading || !categoryGoalQuery.isFetchedAfterMount ?
        <Loading />
      : categoryGoalQuery.isSuccess ?
        <GoalsConfigForm
          initialValues={categoryGoalQuery.data}
          onSubmit={async (values) => {
            const payload = Object.entries(values)
              .filter(([, goal]) => !Number.isNaN(goal.total_sales) && !Number.isNaN(goal.ad_spend))
              .map(([start_date, goal]) => ({
                start_date,
                total_sales_goal: goal.total_sales,
                ad_spend_goal: goal.ad_spend,
                category_id: categoryId,
              }));
            await setGoalsMutation.mutateAsync(payload);
          }}
          displayYear={displayYear}
        />
      : <DevErrorDisplay
          userMessage="There was an error loading the goal configuration"
          error={categoryGoalQuery.error}
        />
      }
    </Modal>
  );
});

interface Props {
  initialValues?: Record<string, Goal>;
  onSubmit: (values: Record<string, Goal>) => void | Promise<void>;
  displayYear: number;
}

function YearControl({
  setDisplayYear,
  displayYear,
}: {
  setDisplayYear: React.Dispatch<React.SetStateAction<number>>;
  displayYear: number;
}) {
  return (
    <HStack alignSelf={"center"}>
      <Button
        variant={"contentOnly"}
        onPress={() => setDisplayYear((year) => year - 1)}
        className={css({
          alignSelf: "end",
          paddingY: "xsmall",
          color: "text.secondary",
          fontSize: "32px",
          fontWeight: "bold",
          lineHeight: "1",
        })}
      >
        {"<"}
      </Button>
      <Datepicker
        label="Goals Year"
        value={new Date(displayYear, 0).toISOString()}
        onChange={(val) => setDisplayYear(new Date(val!).getFullYear())}
        showYearPicker
        dateFormat="yyyy"
      />
      <Button
        variant={"contentOnly"}
        onPress={() => setDisplayYear((year) => year + 1)}
        className={css({
          alignSelf: "end",
          paddingY: "xsmall",
          color: "text.secondary",
          fontSize: "32px",
          fontWeight: "bold",
          lineHeight: "1",
        })}
      >
        {">"}
      </Button>
    </HStack>
  );
}

function GoalsConfigForm({ initialValues, onSubmit, displayYear }: Props) {
  const modal = useModal();
  const form = useFormily<{ perPeriodGoals: Record<string, Goal> }>({
    initialValues: { perPeriodGoals: initialValues },
    effects() {
      onFieldInputValueChange("perPeriodGoals.*.total_sales", (field) => {
        const tacos = field.query(".tacos").value();
        const ad_spend_field = field.query(".ad_spend").take();
        ad_spend_field?.setState((state) => {
          state.value = field.value * (tacos / 100);
        });
      });
      onFieldInputValueChange("perPeriodGoals.*.tacos", (field) => {
        const total_sales = field.query(".total_sales").value();
        const ad_spend_field = field.query(".ad_spend").take();
        ad_spend_field?.setState((state) => {
          state.value = total_sales * (field.value / 100);
        });
      });
      onFieldInputValueChange("perPeriodGoals.*.ad_spend", (field) => {
        const total_sales = field.query(".total_sales").value();
        const tacos_field = field.query(".tacos").take();
        tacos_field?.setState((state) => {
          state.value = (field.value / total_sales) * 100;
        });
      });
    },
  });
  return (
    <FormProvider form={form}>
      <VStack alignItems={"stretch"} paddingX={"xlarge"}>
        <GoalsConfigFormBody displayYear={displayYear} />
        <Center gap="small">
          <Button
            variant="secondary"
            onPress={() => {
              modal.remove();
            }}
          >
            Cancel
          </Button>
          <FormConsumer>
            {(form) => (
              <Button
                isDisabled={form.invalid}
                isPending={form.submitting}
                onPress={() => {
                  void form.submit(async (values) => {
                    await onSubmit(values.perPeriodGoals);
                    modal.remove();
                  });
                }}
              >
                Submit
              </Button>
            )}
          </FormConsumer>
        </Center>
      </VStack>
    </FormProvider>
  );
}

const GoalsConfigFormBody = observer(({ displayYear }: { displayYear: number }) => {
  const inputClasses = inputClassNames();

  const periods = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

  return (
    <ObjectField name="perPeriodGoals">
      {() => {
        return (
          <>
            <Grid gap="small" gridTemplateColumns="1fr 1fr 1fr 1fr 32px">
              <span className={inputClasses.label}>Period</span>
              <span className={inputClasses.label}>Total Sales</span>
              <span className={inputClasses.label}>TACoS</span>
              <span className={inputClasses.label}>Ad Spend</span>
              <span />
            </Grid>
            <VStack gap="xsmall" maxHeight={"450px"} padding="xsmall" overflowY={"auto"}>
              {periods.map((period) => {
                const periodDate = new Date(displayYear, period);
                const periodKey = formatISO(periodDate, { representation: "date" });
                const periodLabel = periodDate.toLocaleString("default", { month: "long" });
                const rowRef = useRef<HTMLDivElement>(null);
                const [isPopoverOpen, setIsPopoverOpen] = useState(false);

                return (
                  <Grid
                    ref={rowRef}
                    key={periodKey}
                    gap="small"
                    gridTemplateColumns="1fr 1fr 1fr 1fr 32px"
                    outline={isPopoverOpen ? "2px solid {colors.trellisPalette.0}" : "none"}
                  >
                    <span>{periodLabel}</span>
                    <ObjectField name={periodKey}>
                      {(field) => (
                        <>
                          <Field
                            name="total_sales"
                            component={[CurrencyInputF, { "aria-label": "Total Sales goal" }]}
                            initialValue={NaN}
                          />
                          <Field
                            name="tacos"
                            component={[NumberInputF, { endIcon: "%", "aria-label": "TACOS goal" }]}
                            initialValue={NaN}
                          />
                          <Field
                            name="ad_spend"
                            component={[CurrencyInputF, { "aria-label": "Ad Spend goal" }]}
                            initialValue={NaN}
                          />
                          <TooltipTrigger>
                            <Button variant="contentOnly" onPress={() => setIsPopoverOpen(true)} fontSize="large">
                              <MdOutlineIosShare
                                className={css({
                                  transform: "rotate(-90deg)",
                                })}
                              />
                            </Button>
                            <Tooltip>Prefill goals using values from other periods</Tooltip>
                          </TooltipTrigger>

                          <Popover
                            triggerRef={rowRef}
                            isOpen={isPopoverOpen}
                            onOpenChange={setIsPopoverOpen}
                            className={cx(popoverStyles)}
                          >
                            <OverlayArrow color={"white"} />
                            <PrefillDialog
                              targetPeriod={periodDate}
                              onApply={(vals) => {
                                field.setValue(vals);
                                setIsPopoverOpen(false);
                              }}
                            />
                          </Popover>
                        </>
                      )}
                    </ObjectField>
                  </Grid>
                );
              })}
            </VStack>
          </>
        );
      }}
    </ObjectField>
  );
});

function PrefillDialog({
  targetPeriod,
  onApply,
}: {
  targetPeriod: Date;
  onApply: (values: { total_sales: number; ad_spend: number; tacos: number }) => void;
}) {
  const [prefillType, setPrefillType] = useState("yoy");
  const [referencePeriod, setReferencePeriod] = useState(subYears(targetPeriod, 1));
  const [totalSalesOffset, setTotalSalesOffset] = useState<Offset>({ amount: 0, type: "percent" });
  const [adSpendOffset, setAdSpendOffset] = useState<Offset>({ amount: 0, type: "percent" });

  const totalsQuery = useQuery(
    useGetV4ProductReportTotalsOpen({
      start: startOfMonth(referencePeriod),
      end: endOfMonth(referencePeriod),
    }),
  );

  const totalSalesGoal =
    totalsQuery.data?.data.total_sales ?
      applyOffset(totalsQuery.data.data.total_sales, totalSalesOffset.amount, totalSalesOffset.type)
    : NaN;

  const adSpendGoal =
    totalsQuery.data?.data.cost ?
      applyOffset(totalsQuery.data.data.cost, adSpendOffset.amount, adSpendOffset.type)
    : NaN;

  return (
    <Dialog
      className={vstack({
        padding: "xlarge",
      })}
    >
      <HStack gap="large" alignItems="center">
        <Select
          label={"Reference Period"}
          className={css({ width: "300px" })}
          items={[
            { value: "yoy", label: "Year over Year" },
            { value: "mom", label: "Month over Month" },
            { value: "custom", label: "Custom" },
          ]}
          selectedKey={prefillType}
          onSelectionChange={(val) => {
            setPrefillType(val as string);
            if (val === "yoy") {
              setReferencePeriod(subYears(targetPeriod, 1));
            } else if (val === "mom") {
              setReferencePeriod(subMonths(targetPeriod, 1));
            }
          }}
        />
        <Datepicker
          disabled={prefillType !== "custom"}
          label="&nbsp;"
          value={referencePeriod.toISOString()}
          onChange={(val) => setReferencePeriod(new Date(val!))}
          showMonthYearPicker
          dateFormat={"yyyy, MMMM"}
        />
      </HStack>
      <HStack>
        <CurrencyInput
          label={"Total Sales in Reference Period"}
          isDisabled
          value={totalsQuery.data?.data.total_sales ?? NaN}
        />
        <OffsetInput label={"Offset By"} value={totalSalesOffset} onChange={setTotalSalesOffset} />
        <CurrencyInput label={"Total Sales Goal"} isDisabled value={totalSalesGoal} />
      </HStack>
      <HStack>
        <CurrencyInput label={"Ad Spend in Reference Period"} isDisabled value={totalsQuery.data?.data.cost ?? NaN} />
        <OffsetInput label={"Offset By"} value={adSpendOffset} onChange={setAdSpendOffset} />
        <CurrencyInput label={"Ad Budget"} isDisabled value={adSpendGoal} />
      </HStack>
      <NumberInput
        label="TACoS Goal"
        endIcon="%"
        value={adSpendGoal && totalSalesGoal ? (adSpendGoal / totalSalesGoal) * 100 : NaN}
        isDisabled
      />
      <Center>
        <Button
          onPress={() =>
            onApply({
              total_sales: totalSalesGoal,
              ad_spend: adSpendGoal,
              tacos: totalSalesGoal !== 0 ? (adSpendGoal / totalSalesGoal) * 100 : 0,
            })
          }
        >
          Apply
        </Button>
      </Center>
    </Dialog>
  );
}
